import { memo, useState } from 'react';
import { Edit, ExpandMore } from '@mui/icons-material';
import {
  IconButton,
  Accordion,
  AccordionDetails,
  Divider,
  Grid,
  Typography,
  useEventCallback,
} from '@mui/material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { noop } from 'shell/utils/noop';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { useFormikContext } from 'formik';
import Delete from '@mui/icons-material/Delete';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { HistoryCohabitationVM } from 'cohabitation/view-models/history-cohabitation';
import ModalInsertCohabitationData from './modify-historian-modal';

interface AccordionHistorianFamilyDataProps {
  isEdit?: boolean;
}

const AccordionHistorianFamilyDataComponent = ({
  isEdit,
}: AccordionHistorianFamilyDataProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(false);

  const [openModal, setOpenModal] = useState<boolean>(false);

  const formContext = useFormikContext<HistoryCohabitationVM>();

  const navigate = useNavigate();

  const { historyDataManagement } = formContext.values;

  const deleteData = useHttpClient(`${__PERSON_API_URL__}/persona/convivenza`, {
    options: {
      method: 'DELETE',
      data: {
        codice: formContext.values.cohabitationData?.idSipo,
      },
    },
  });

  const updateData = useHttpClient(`${__PERSON_API_URL__}/persona/convivenza/dati`, {
    options: { method: 'PUT' },
  });

  const onEditPress = useEventCallback(() => {
    setOpenModal(true);
  });

  const onDeletePress = useEventCallback(() => {
    (deleteData() as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate('/convivenza/ricerca');
    });
  });

  return (
    <Accordion expanded={open} onChange={() => setOpen(!open)}>
      <AccordionSummary
        aria-controls="panel-historian-cohabitation-data-content"
        id="panel-historian-cohabitation-data-content"
        expandIcon={<ExpandMore />}
      >
        <Typography>Gestione Storico dati convivenza</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={7}>
              <PmiTextField
                id="name"
                label="Nome Convivenza"
                aria-label="Nome Convivenza"
                fullWidth
                disabled
                value={historyDataManagement?.name || ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                id="type"
                label="Tipo Convivenza"
                aria-label="Tipo Convivenza"
                fullWidth
                disabled
                value={historyDataManagement?.type || ''}
              />
            </Grid>
            {isEdit ? (
              <Grid item xs={12} md={1}>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onEditPress();
                  }}
                  aria-label="Modifica elemento"
                  aria-haspopup="true"
                  style={{
                    padding: '4px',
                  }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onDeletePress();
                  }}
                  aria-label="Elimina elemento"
                  aria-haspopup="true"
                  style={{
                    padding: '4px',
                  }}
                >
                  <Delete />
                </IconButton>
              </Grid>
            ) : undefined}
          </Grid>
          <ModalInsertCohabitationData
            modalData={{
              type: historyDataManagement?.type || '',
              institutionDate: historyDataManagement?.institutionDate || null,
              closeDate: historyDataManagement?.closeDate || null,
              name: historyDataManagement?.name || '',
              fiscalCode: historyDataManagement?.fiscalCode || '',
              phone1: historyDataManagement?.phone1 || '',
              phone2: historyDataManagement?.phone2 || '',
              phone3: historyDataManagement?.phone3 || '',
            }}
            open={openModal}
            onClose={() => setOpenModal(false)}
            onSubmit={(values) => {
              (
                updateData({
                  data: {
                    tipo: values.type,
                    nome: values.name,
                    telefono1: values.phone1,
                    telefono2: values.phone2,
                    telefono3: values.phone3,
                    dataCessazione: values.closeDate?.toFormat('yyyyMMdd'),
                    dataIstituzione: values.institutionDate?.toFormat('yyyyMMdd'),
                    key: {
                      codice: formContext.values.cohabitationData?.idSipo,
                    },
                  },
                }) as ResponsePromise<void>
              ).then(() => {
                // chiamata be
                formContext.setValues({
                  ...formContext.values,
                  historyDataManagement: {
                    ...(formContext.values.historyDataManagement || {}),
                    ...values,
                  },
                });
                toast.success('Operazione eseguita con successo');
                setOpenModal(false);
              });
            }}
          />
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data istituzione"
                aria-label="Data istituzione"
                value={historyDataManagement?.institutionDate || null}
                onChange={noop}
                disabled
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="institutionDate"
                    helperText=""
                    error={false}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data chiusura"
                aria-label="Data chiusura"
                value={historyDataManagement?.closeDate || null}
                onChange={noop}
                disabled
                renderInput={(params) => (
                  <PmiTextField {...params} id="closeDate" helperText="" error={false} fullWidth />
                )}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <PmiTextField
                id="phone1"
                label="Telefono"
                aria-label="Telefono"
                fullWidth
                disabled
                value={historyDataManagement?.phone1 || ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                id="phone2"
                fullWidth
                disabled
                value={historyDataManagement?.phone2 || ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                id="phone3"
                fullWidth
                disabled
                value={historyDataManagement?.phone3 || ''}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              id="fiscalCode"
              label="Codice Fiscale"
              aria-label="Codice Fiscale"
              fullWidth
              disabled
              value={historyDataManagement?.fiscalCode || ''}
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTitleDivider>Capo Convivenza</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              id="ultimoIs"
              label="Ultimo Capo Convivenza"
              aria-label="Ultimo is"
              fullWidth
              disabled
              value={historyDataManagement?.lastHeadCohabitation || ''}
            />
          </Grid>
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Nato il"
                aria-label="Nato il"
                value={historyDataManagement?.birthHeadCohabitation || null}
                onChange={noop}
                disabled
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="dataNascita"
                    helperText=""
                    error={false}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PmiTextField
                id="luogoNascita"
                label="A"
                aria-label="A"
                fullWidth
                disabled
                value={historyDataManagement?.placeHeadCohabitation || ''}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <PmiTextField
                id="sesso"
                label="Sesso"
                aria-label="Sesso"
                fullWidth
                disabled
                value={historyDataManagement?.genderHeadCohabitation || ''}
              />
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

AccordionHistorianFamilyDataComponent.displayName = 'Change control data accordion component';

const AccordionHistorianFamilyData = memo(AccordionHistorianFamilyDataComponent);

export default AccordionHistorianFamilyData;
